import { QuestionAnswerMap } from '@care-portals/api-interfaces';
import { GenericTerminalQuestionFieldAnswers } from '../../Generic/questions';
import { MODAFINIL_THERAPY_CONSENT } from '../initial/questions';

const modafinilFollowupQuestions = [
  {
    field: 'modafinilFollowup_sexAssignedAtBirth',
    label: 'What was your sex assigned at birth?',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    }
  },
  {
    field: 'modafinilFollowup_treatmentProgress',
    label: 'How are you doing on the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'The medication is helping with my symptoms',
          key: 'helping'
        },
        {
          value: "I'm not doing well with treatment",
          key: 'not_well'
        }
      ]
    }
  },
  {
    field: 'modafinilFollowup_treatmentProgressDetails',
    label: 'Please tell us more about what you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'modafinilFollowup_treatmentProgressDetails'
    }
  },
  {
    field: 'modafinilFollowup_sideEffects',
    label:
      'Have you experienced any side effects from the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'modafinilFollowup_sideEffectsDetails',
    label: 'Please tell us more about the side effects you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'modafinilFollowup_sideEffectsDetails'
    }
  },
  {
    field: 'modafinilFollowup_healthChanges',
    label:
      'Have there been any health or medication changes, ED visits or hospitalizations since your last check-in?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'modafinilFollowup_healthChangesDetails',
    label: 'Please tell us more about the changes in your health',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'modafinilFollowup_healthChangesDetails'
    }
  },
  {
    field: 'modafinilFollowup_bloodPressure',
    label:
      'Blood pressure monitoring is important while on modafinil. What was your most recent blood pressure reading? Please select the closest range. If your top and bottom numbers fall into different ranges, choose the higher range.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Low: Less than 90/60', key: 'low' },
        {
          value: 'Normal or mildly elevated: Less than 130/80',
          key: 'normal'
        },
        {
          value: 'High: 130–159 (top number) or 80–99 (bottom number)',
          key: 'high'
        },
        {
          value: 'Very high: 160–179 (top number) or 100–119 (bottom number)',
          key: 'very_high'
        },
        {
          value:
            'Severely high: 180 or higher (top number) or 120 or higher (bottom number)',
          key: 'severely_high'
        }
      ]
    }
  },
  {
    field: 'modafinilFollowup_heartRate',
    label:
      'Pulse-rate monitoring is important while taking modafinil. What was your most recent resting heart rate? Please select the closest range. Measure your pulse while seated, calm, and at rest.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Low: Less than 50 beats per minute', key: 'low' },
        { value: 'Mildly low: 50–59 beats per minute', key: 'mildly_low' },
        { value: 'Normal: 60–100 beats per minute', key: 'normal' },
        { value: 'High: 101 beats per minute or more', key: 'high' }
      ]
    }
  },
  {
    field: 'modafinilFollowup_treatmentPreference',
    label:
      'Would you like to continue your current treatment, change the dose, or change the medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: "I'm doing well and I would like to continue what I'm on",
          key: 'continue'
        },
        {
          value:
            'I like the medication but I would like to increase the dose if there is a higher dose',
          key: 'increase_dose'
        },
        {
          value:
            'I like the medication but I would like to decrease the dose if there is a lower dose',
          key: 'decrease_dose'
        },
        {
          value: "I'm not sure, have the provider reach out to discuss further",
          key: 'provider_reach_out'
        }
      ]
    }
  },
  {
    field: 'modafinilFollowup_additionalQuestions',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'modafinilFollowup_additionalQuestions'
    }
  },
  {
    field: 'modafinilFollowup_treatmentConsent',
    label: 'Consent (Modafinil Therapy):',
    type: 'select',
    educationalSlide: MODAFINIL_THERAPY_CONSENT,
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'do_not_continue'
        },
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  }
] as const;

export type ModafinilFollowupQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof modafinilFollowupQuestions>
> &
  GenericTerminalQuestionFieldAnswers;

export default modafinilFollowupQuestions;
